import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, TextInput, View } from 'react-native';

import {
  ActionSheet,
  Button,
  FormControlTextInput,
  IconClose,
  IconOffer,
  IconOfferOutline,
  Text,
  createMqStyles,
  tokens,
} from '@fhs/ui';

import { useRedeemPromoCode } from './use-redeem-promo-code';
export interface PromoCodeDrawerProps {
  isOpen: boolean;
  onDismiss: () => void;
  promoCode?: string;
}

export enum PromoCodeErrorReason {
  redeemedCode = 'REDEEMED_CODE',
  expiredCode = 'EXPIRED_CODE',
  invalidCode = 'INVALID_CODE',
}

export class PromoCodeError extends Error {
  public message: PromoCodeErrorReason;

  constructor(message: PromoCodeErrorReason) {
    super();
    this.message = message;
  }
}

const PROMO_MODAL_TITLE = 'Enable Exclusive Offer';
const PROMO_MODAL_INTRO =
  'If you received a promo code, you can enable an exclusive offer.';

export const PromoCodeDrawer = ({ isOpen, onDismiss, promoCode }: PromoCodeDrawerProps) => {
  const [promoCodeValue, setPromoCodeValue] = useState(promoCode ?? '');
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const mqStyles = useMqStyles();
  const {
    redeemPromoCode,
    errorMessage: redeemErrorMessage,
    isRedeeming,
  } = useRedeemPromoCode({ onDismiss });
  const inputRef = useRef<TextInput>(null);
  const headerSummaryRef = useRef<View>(null);
  const errorMessage = redeemErrorMessage || formErrorMessage;

  const headerSummaryA11yLabel = useMemo(
    () => `${PROMO_MODAL_TITLE}. ${PROMO_MODAL_INTRO}`,
    []
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    /**
     * Web: focus the heading block first so SR + Tab get one stop that reads title + intro (dialog
     * stays “Promo code” so we don’t triple the headline). Native: focus the field for typing.
     */
    const id = setTimeout(() => {
      if (Platform.OS === 'web') {
        (headerSummaryRef.current as unknown as { focus?: () => void } | null)?.focus?.();
        return;
      }
      inputRef.current?.focus();
    }, 150);
    return () => clearTimeout(id);
  }, [isOpen]);

  const onPromoCodeChange = (value: string) => {
    setPromoCodeValue(value);
    setFormErrorMessage('');
  };

  const validatePromoCode = (value: string) => {
    if (value.length < 1) {
      return 'Please enter a promo code.';
    }

    return true;
  };

  const onSubmit = () => {
    const validationResult = validatePromoCode(promoCodeValue);
    if (validationResult === true) {
      redeemPromoCode(promoCodeValue);
      return;
    }

    setFormErrorMessage(validationResult);
  };

  return (
    <ActionSheet
      grabber
      isVisible={isOpen}
      onClose={onDismiss}
      /** Short dialog name only — visible heading below carries “Enable Exclusive Offer” (avoid SR/tab repeating the same phrase from dialog + heading + close). */
      sheetAccessibilityLabel="Promo code"
    >
      <View style={mqStyles.modalContainer}>
        <View style={styles.headerContainer}>
          <View
            accessibilityElementsHidden
            accessible={false}
            importantForAccessibility="no-hide-descendants"
            style={styles.iconCircle}
          >
            <IconOfferOutline size={40} color={tokens.colors.$black} />
          </View>
          <View
            ref={headerSummaryRef}
            accessibilityLabel={headerSummaryA11yLabel}
            accessibilityRole="text"
            accessible
            focusable
            style={styles.headerSummary}
            {...(Platform.OS === 'web'
              ? ({
                  tabIndex: 0,
                } as Record<string, unknown>)
              : {})}
          >
            <Text.Ui accessible={false} size="lg" weight="semibold" style={mqStyles.headerTitle}>
              {PROMO_MODAL_TITLE}
            </Text.Ui>
            <Text.Paragraph
              accessible={false}
              ellipsizeMode="tail"
              numberOfLines={2}
              size="lg"
              style={styles.headerDescription}
            >
              {PROMO_MODAL_INTRO}
            </Text.Paragraph>
          </View>
          <Button
            accessibilityLabel="Close"
            accessibilityHint="Closes promo code dialog"
            size="md"
            style={styles.closeButton}
            type={'unstyled'}
            aria-label="Close"
            onPress={() => onDismiss()}
          >
            <Button.Icon>
              <IconClose accessibilityElementsHidden importantForAccessibility="no-hide-descendants" />
            </Button.Icon>
          </Button>
        </View>
        <View style={styles.formContainer}>
          <View style={styles.inputContainer}>
            <Text.Ui accessible={false} size="md" weight="semibold">
              Promo Code
            </Text.Ui>
            <FormControlTextInput
              ref={inputRef}
              placeholder={'Enter your code'}
              accessibilityLabel="Enter your promo code"
              onChangeText={onPromoCodeChange}
              value={promoCodeValue}
              leftIcon={
                <IconOffer
                  accessibilityElementsHidden
                  aria-hidden
                  importantForAccessibility="no-hide-descendants"
                  size={24}
                  style={mqStyles.inputLeftIcon}
                />
              }
              style={{
                borderColor: errorMessage ? tokens.colors.$error : tokens.colors.$houseDark,
              }}
            />
            {errorMessage && (
              <Text.Ui size={'sm'} style={styles.errorMessage}>
                {errorMessage}
              </Text.Ui>
            )}
          </View>
          <View>
            <Button
              accessibilityLabel="Apply promo code"
              style={styles.applyButton}
              loading={isRedeeming}
              disabled={isRedeeming}
              size="xl"
              onPress={onSubmit}
            >
              <Button.Text>Apply Promo Code</Button.Text>
            </Button>
          </View>
        </View>
      </View>
    </ActionSheet>
  );
};

const styles = StyleSheet.create({
  applyButton: {
    height: 48,
    marginTop: 20,
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: -9,
    borderColor: tokens.colors.$transparent,
    borderWidth: 0,
  },
  formContainer: {
    width: '100%',
    flexDirection: 'column',
    marginTop: 16,
  },
  inputContainer: {
    gap: 4,
  },
  headerContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
  },
  headerSummary: {
    width: '100%',
    alignItems: 'center',
  },
  headerDescription: {
    textAlign: 'center',
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  iconCircle: {
    height: 64,
    width: 64,
    borderRadius: 200,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colors.$houseLight,
  },
  errorMessage: {
    color: tokens.colors.$error,
  },
});

const useMqStyles = createMqStyles({
  modalContainer: {
    $base: {
      borderRadius: 12,
      paddingBottom: 16,
      paddingTop: 20,
      paddingHorizontal: 16,
    },
    $gteDesktop: {
      paddingHorizontal: 24,
      paddingBottom: 28,
      paddingTop: 28,
    },
  },
  inputLeftIcon: {
    $base: {
      marginRight: 8,
      color: tokens.colors.$blackOpacity30,
    },
    $ltDesktop: {
      color: tokens.colors.$blackOpacity75,
    },
  },
  headerTitle: {
    $base: {
      paddingTop: 8,
    },
    $gteDesktop: {
      fontSize: 24,
    },
  },
});
